import { useEffect, useState } from "react";
import type { PublicPlayer } from "backend/types";
import { api, getToken, setToken } from "./api";
import { Lobby } from "./Lobby";
import { Signup } from "./Signup";
import { GameView } from "./GameView";
import { ReviewPage } from "./ReviewPage";
import { Avatar } from "./ui";

type Route = { kind: "lobby" } | { kind: "game"; id: string } | { kind: "review"; id: string };

function readRoute(): Route {
  const h = window.location.hash.replace(/^#\/?/, "");
  const m = h.match(/^game\/([\w-]+)$/);
  if (m) return { kind: "game", id: m[1] };
  const r = h.match(/^review\/([\w-]+)$/);
  if (r) return { kind: "review", id: r[1] };
  return { kind: "lobby" };
}

export function navigate(route: Route): void {
  const hash =
    route.kind === "game" ? `#/game/${route.id}` : route.kind === "review" ? `#/review/${route.id}` : "#/";
  if (window.location.hash !== hash) window.location.hash = hash;
}

export function App() {
  const [me, setMe] = useState<PublicPlayer | null>(null);
  const [bootDone, setBootDone] = useState(false);
  const [route, setRoute] = useState<Route>(() => readRoute());

  useEffect(() => {
    const onHash = () => setRoute(readRoute());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    (async () => {
      if (!getToken()) return setBootDone(true);
      try {
        const r = await api.me();
        setMe(r.player);
      } catch {
        setToken(null);
      } finally {
        setBootDone(true);
      }
    })();
  }, []);

  if (!bootDone) return <div className="p-8 text-ink/50">Loading…</div>;

  if (!me) {
    return (
      <Signup
        onSignedUp={(p) => {
          setToken(p.token);
          setMe({ id: p.id, name: p.name, color: p.color });
          navigate({ kind: "lobby" });
        }}
      />
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <header className="flex items-center justify-between px-4 sm:px-6 py-2.5 bg-wa text-white shadow-sm">
        <button
          onClick={() => navigate({ kind: "lobby" })}
          className="font-display text-xl font-bold tracking-tight flex items-center gap-2"
        >
          🛍️ dukaan
        </button>
        <div className="flex items-center gap-3">
          <Avatar color={me.color} size={26} ring />
          <span className="text-sm font-medium">{me.name}</span>
          <button
            onClick={() => {
              setToken(null);
              setMe(null);
              navigate({ kind: "lobby" });
            }}
            className="text-xs text-white/70 hover:text-white"
          >
            sign out
          </button>
        </div>
      </header>

      <main className="flex-1 flex flex-col">
        {route.kind === "lobby" && <Lobby onOpen={(id) => navigate({ kind: "game", id })} />}
        {route.kind === "game" && (
          <GameView gameId={route.id} me={me} onLeave={() => navigate({ kind: "lobby" })} />
        )}
        {route.kind === "review" && (
          <ReviewPage gameId={route.id} onLeave={() => navigate({ kind: "lobby" })} />
        )}
      </main>
    </div>
  );
}
